import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, ArrowLeft, Trash2, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function AnnouncementPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [msgEn, setMsgEn] = useState("");
  const [msgAr, setMsgAr] = useState("");
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [translating, setTranslating] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/announcement");
      setCurrent(data.announcement);
    } catch (_e) {
      void _e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/settings" replace />;

  const translate = async (target) => {
    const source = target === "ar" ? msgEn : msgAr;
    if (!source.trim()) return;
    setTranslating(target);
    try {
      const { data } = await api.post("/admin/announcement/translate", {
        text: source.trim(),
        target,
      });
      if (target === "ar") setMsgAr(data.translated);
      else setMsgEn(data.translated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating("");
    }
  };

  const publish = async () => {
    if (!msgEn.trim() && !msgAr.trim()) return;
    setPublishing(true);
    try {
      await api.post("/admin/announcement", {
        message_en: msgEn.trim(),
        message_ar: msgAr.trim(),
      });
      toast.success(t("ann.published"));
      setMsgEn("");
      setMsgAr("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPublishing(false);
    }
  };

  const clear = async () => {
    setClearing(true);
    try {
      await api.delete("/admin/announcement");
      toast.success(t("ann.cleared"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="announcement-page">
      <button
        type="button"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1.5 text-xs font-semibold text-espresso/60 hover:text-espresso transition-colors"
        data-testid="announcement-back-button"
      >
        <ArrowLeft className="w-3.5 h-3.5 rtl:rotate-180" />
        {t("account.title")}
      </button>
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-olive" />
          {t("ann.title")}
        </h1>
        <p className="text-sm text-espresso/70 mt-1">{t("ann.sub")}</p>
      </div>

      <div className="bg-white rounded-xl border border-app-border p-5 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-widest text-olive font-bold">
              {t("ann.msg_en")}
            </label>
            <button
              type="button"
              onClick={() => translate("en")}
              disabled={!msgAr.trim() || !!translating}
              className="flex items-center gap-1 text-[11px] font-semibold text-olive hover:text-olive/80 disabled:opacity-40 transition-colors"
              data-testid="translate-to-en-button"
            >
              {translating === "en" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
              {t("ann.translate_to_en")}
            </button>
          </div>
          <Textarea
            value={msgEn}
            onChange={(e) => setMsgEn(e.target.value)}
            placeholder={t("ann.placeholder")}
            rows={4}
            dir="ltr"
            data-testid="announcement-textarea-en"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-widest text-olive font-bold">
              {t("ann.msg_ar")}
            </label>
            <button
              type="button"
              onClick={() => translate("ar")}
              disabled={!msgEn.trim() || !!translating}
              className="flex items-center gap-1 text-[11px] font-semibold text-olive hover:text-olive/80 disabled:opacity-40 transition-colors"
              data-testid="translate-to-ar-button"
            >
              {translating === "ar" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
              {t("ann.translate_to_ar")}
            </button>
          </div>
          <Textarea
            value={msgAr}
            onChange={(e) => setMsgAr(e.target.value)}
            placeholder={t("ann.placeholder_ar")}
            rows={4}
            dir="rtl"
            data-testid="announcement-textarea-ar"
          />
        </div>
        <p className="text-[11px] text-espresso/50">{t("ann.push_note")}</p>
        <Button
          onClick={publish}
          disabled={publishing || (!msgEn.trim() && !msgAr.trim())}
          className="bg-olive hover:bg-olive/90"
          data-testid="announcement-publish-button"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : t("ann.publish")}
        </Button>
      </div>

      <div>
        <h2 className="text-xs font-bold uppercase tracking-widest text-olive mb-2">{t("ann.current")}</h2>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-espresso/50" />
        ) : current ? (
          <div className="bg-olive/5 border border-olive/30 rounded-xl p-4 space-y-2" data-testid="announcement-current">
            {current.message_en && (
              <div className="text-sm text-espresso whitespace-pre-wrap" dir="ltr">{current.message_en}</div>
            )}
            {current.message_ar && (
              <div className="text-sm text-espresso whitespace-pre-wrap border-t border-olive/20 pt-2" dir="rtl">
                {current.message_ar}
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <span className="text-[11px] text-espresso/50">
                {new Date(current.created_at).toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={clear}
                disabled={clearing}
                className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
                data-testid="announcement-clear-button"
              >
                {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><Trash2 className="w-3.5 h-3.5 mr-1" />{t("ann.clear")}</>)}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-espresso/50" data-testid="announcement-none">{t("ann.none")}</div>
        )}
      </div>
    </div>
  );
}
